// ============================================================
// Prepared answer for the `reply-parse` AI task (#65): a small
// keyword parser for carrier replies in PL / CS / DE / EN. Used
// when there is no API key or no Worker. Plain JS without aliases:
// the Worker imports it too (worker/ai/replyParse.js).
// ============================================================

const has = (re, s) => re.test(s);

const NOT_COMING = /nie (przyjedzie|dojedzie|będzie|bedzie)|odwoł|anul|nepřijede|nepojede|nedorazí|zrušen|kommt nicht|nicht kommen|storn|cancel|won'?t come/;
const NO_POWER = /pauz|czas pracy|odpoczyn|bez výkonu|výkon|přestávk|lenkzeit|ruhezeit|driving hours|rest/;
const TOMORROW = /jutro|zítra|zitra|morgen|tomorrow/;
const EN_ROUTE = /w drodze|jadę|jade|jedu|na cestě|na ceste|unterwegs|on the way|korek|zácp|zacp|stau|traffic/;
const CONFIRMED = /\bok\b|\btak\b|\bano\b|jasne|zgodnie z planem|podle plánu|wie geplant|potwierdzam|potvrzuj|bestätig|confirm/;

const REASONS = [
  [/korek|zácp|zacp|stau|traffic/, 'zácpa na cestě'],
  [/awari|porucha|panne|defekt|breakdown/, 'porucha vozidla'],
  [/pauz|czas pracy|odpoczyn|výkon|přestávk|lenkzeit|ruhezeit|rest/, 'povinná přestávka řidiče'],
  [/granic|hranic|grenze|border/, 'čekání na hranici'],
  [/załadun|nakládk|beladung|other loading/, 'zdržení na předchozí nakládce'],
];

const pad = (n) => String(n).padStart(2, '0');
const toMinutes = (hhmm) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };

// First explicit clock time ("15:30", "o 15", "v 15", "um 15 Uhr", "15h") or a relative one ("za 2 godziny").
function findTime(s, now) {
  const clock = s.match(/\b([01]?\d|2[0-3])[:.]([0-5]\d)\b/);
  if (clock) return `${pad(clock[1])}:${clock[2]}`;
  const hour = s.match(/\b(?:o|v|ve|um|at|około|kolem|gegen)\s+([01]?\d|2[0-3])\b|\b([01]?\d|2[0-3])\s?(?:h|hod|godz|uhr)\b/);
  if (hour) return `${pad(hour[1] || hour[2])}:00`;
  const rel = s.match(/\b(?:za|in|v)\s+(\d{1,2})\s?(min|godz|hod|stund|hour|h)/);
  if (rel && now) {
    const add = Number(rel[1]) * (rel[2].startsWith('min') ? 1 : 60);
    const total = toMinutes(now) + add;
    return `${pad(Math.floor(total / 60) % 24)}:${pad(total % 60)}`;
  }
  return null;
}

// Same output shape as the AI: state, time, dayOffset, reason (CS), translationCs, confidence.
export function parseReplyDemo({ text, plannedAt, now }) {
  const s = text.toLowerCase();
  const time = findTime(s, now);
  const dayOffset = has(TOMORROW, s) ? 1 : time ? 0 : null;
  const reason = REASONS.find(([re]) => re.test(s))?.[1] ?? null;
  const out = { time, dayOffset, reason, translationCs: null };

  if (has(NOT_COMING, s)) return { ...out, state: 'not-coming', time: null, dayOffset: has(TOMORROW, s) ? 1 : null, confidence: 0.9 };
  if (has(NO_POWER, s) && (time || dayOffset)) return { ...out, state: 'no-power', confidence: 0.85 };
  if (dayOffset === 1) return { ...out, state: 'rescheduled', confidence: time ? 0.9 : 0.6 };
  if (time) {
    const late = plannedAt && Math.abs(toMinutes(time) - toMinutes(plannedAt)) > 30;
    if (has(EN_ROUTE, s)) return { ...out, state: 'en-route', confidence: 0.9 };
    return { ...out, state: late ? 'rescheduled' : 'confirmed', confidence: 0.8 };
  }
  if (has(EN_ROUTE, s)) return { ...out, state: 'en-route', confidence: 0.6 };
  if (has(CONFIRMED, s)) return { ...out, state: 'confirmed', confidence: 0.8 };
  return { ...out, state: 'unclear', time: null, dayOffset: null, confidence: 0.3 };
}
